import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import DiscountOutlinedIcon from "@mui/icons-material/DiscountOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import baseUrl from "../../../api/baseUrl";
import FormDate from "../../../components/formFeild/FormDate";
import PaymentReportCharts, { PaymentTrendPoint } from "./PaymentReportCharts";

type FormData = {
  startDate: string;
  endDate: string;
};

type DatePreset = "this-month" | "last-month" | "last-30" | "this-year" | "all";

type PaymentSummary = {
  totalSum?: number;
  discountSum?: number;
  cardSum?: number;
  cashSum?: number;
  bankTransferSum?: number;
  payableSum?: number;
  dueSum?: number;
  netotalSum?: number;
  lastDueSum?: number;
};

type SummaryCard = {
  key: string;
  label: string;
  hint?: string;
  icon: React.ElementType;
  accent?: boolean;
  warn?: boolean;
  computed?: boolean;
};

const FINANCIAL_CARDS: SummaryCard[] = [
  {
    key: "totalSum",
    label: "Total billed",
    hint: "Sum of all invoice totals",
    icon: AccountBalanceWalletOutlinedIcon,
    accent: true,
  },
  {
    key: "netotalSum",
    label: "Net revenue",
    hint: "After discounts applied",
    icon: TrendingUpOutlinedIcon,
  },
  {
    key: "paid",
    label: "Collected",
    hint: "Cash + card + transfer",
    icon: PaidOutlinedIcon,
    computed: true,
  },
  {
    key: "dueSum",
    label: "Outstanding",
    hint: "Balance still due",
    icon: WarningAmberOutlinedIcon,
    warn: true,
  },
  {
    key: "discountSum",
    label: "Discounts",
    hint: "Total discounts given",
    icon: DiscountOutlinedIcon,
  },
];

const METHOD_CARDS: SummaryCard[] = [
  { key: "cashSum", label: "Cash", icon: LocalAtmOutlinedIcon },
  { key: "cardSum", label: "Card", icon: CreditCardOutlinedIcon },
  { key: "bankTransferSum", label: "Transfer", icon: AccountBalanceOutlinedIcon },
];

const DATE_PRESETS: { id: DatePreset; label: string }[] = [
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "last-30", label: "Last 30 days" },
  { id: "this-year", label: "This year" },
  { id: "all", label: "All time" },
];

function toDateInput(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getPresetRange(preset: DatePreset): { startDate: string; endDate: string } | null {
  const today = new Date();
  const end = toDateInput(today);

  if (preset === "all") return null;

  if (preset === "this-month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { startDate: toDateInput(start), endDate: end };
  }

  if (preset === "last-month") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endDate = new Date(today.getFullYear(), today.getMonth(), 0);
    return { startDate: toDateInput(start), endDate: toDateInput(endDate) };
  }

  if (preset === "last-30") {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { startDate: toDateInput(start), endDate: end };
  }

  const start = new Date(today.getFullYear(), 0, 1);
  return { startDate: toDateInput(start), endDate: end };
}

function matchPreset(startDate?: string, endDate?: string): DatePreset | null {
  if (!startDate || !endDate) return "all";
  for (const preset of DATE_PRESETS) {
    const range = getPresetRange(preset.id);
    if (range?.startDate === startDate && range?.endDate === endDate) {
      return preset.id;
    }
  }
  return null;
}

function getPresetLabel(preset: DatePreset) {
  return DATE_PRESETS.find((item) => item.id === preset)?.label ?? "Custom range";
}

function formatAmount(value?: number) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function getSummaryValue(count: PaymentSummary, key: string, computed?: boolean) {
  if (computed) {
    return (
      Number(count?.bankTransferSum ?? 0) +
      Number(count?.cashSum ?? 0) +
      Number(count?.cardSum ?? 0)
    );
  }
  return Number((count as Record<string, number | undefined>)?.[key] ?? 0);
}

function PanelCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-200/40 dark:border-slate-700/80 dark:bg-slate-800/60 dark:shadow-none ${className}`}
    >
      {children}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
        <Icon sx={{ fontSize: 20 }} />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white tracking-tight">
          {title}
        </h3>
        {subtitle ? (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

function SummaryCardTile({
  card,
  value,
  loading,
}: {
  card: SummaryCard;
  value: number;
  loading: boolean;
}) {
  const Icon = card.icon;
  const isOutstanding = card.key === "dueSum" && value > 0;

  return (
    <div
      className={`rounded-xl border p-4 ${
        card.accent
          ? "border-primary/20 bg-gradient-to-br from-primary/[0.07] to-white dark:from-primary/15 dark:to-slate-800/80 dark:border-primary/30"
          : isOutstanding
            ? "border-amber-200 bg-amber-50/80 dark:border-amber-700/50 dark:bg-amber-950/20"
            : "border-slate-100 bg-white dark:border-slate-700/60 dark:bg-slate-800/40"
      }`}
    >
      <div
        className={`mb-2 flex h-9 w-9 items-center justify-center rounded-lg ${
          card.accent
            ? "bg-primary/15 text-primary"
            : isOutstanding
              ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300"
        }`}
      >
        <Icon sx={{ fontSize: 18 }} />
      </div>
      <p
        className={`text-lg font-bold tabular-nums ${
          card.accent
            ? "text-primary"
            : isOutstanding
              ? "text-amber-700 dark:text-amber-300"
              : "text-slate-900 dark:text-white"
        }`}
      >
        {loading ? "…" : formatAmount(value)}
      </p>
      <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {card.label}
      </p>
      {card.hint ? (
        <p className="mt-1 text-[10px] leading-snug text-slate-400 dark:text-slate-500">
          {card.hint}
        </p>
      ) : null}
    </div>
  );
}

export default function PaymentReportTabPanel() {
  const [summary, setSummary] = useState<PaymentSummary>({});
  const [trend, setTrend] = useState<PaymentTrendPoint[]>([]);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [activePreset, setActivePreset] = useState<DatePreset | null>("this-month");
  const isApplyingPreset = useRef(false);
  const methods = useForm<FormData>();
  const { watch, reset } = methods;

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const loadSummary = useCallback(async (rangeStart?: string, rangeEnd?: string) => {
    setLoading(true);
    try {
      const params =
        rangeStart && rangeEnd
          ? { startDate: rangeStart, endDate: rangeEnd }
          : undefined;

      const [countResponse, trendResponse] = await Promise.all([
        baseUrl.get("payment/getAllPaymentCount", { params }),
        baseUrl.get("payment/getPaymentChartTrend", { params }),
      ]);

      setSummary(countResponse.data?.data ?? {});
      setInvoiceCount(Number(countResponse.data?.invoiceCount ?? 0));
      setTrend(trendResponse.data?.data ?? []);
    } catch (error) {
      console.error(error);
      setSummary({});
      setTrend([]);
      setInvoiceCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyPreset = useCallback(
    (preset: DatePreset) => {
      isApplyingPreset.current = true;
      setActivePreset(preset);
      const range = getPresetRange(preset);
      if (!range) {
        reset({ startDate: "", endDate: "" });
        loadSummary();
      } else {
        reset(range);
        loadSummary(range.startDate, range.endDate);
      }
      window.setTimeout(() => {
        isApplyingPreset.current = false;
      }, 0);
    },
    [loadSummary, reset]
  );

  useEffect(() => {
    applyPreset("this-month");
  }, [applyPreset]);

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      if (isApplyingPreset.current) return;

      if (value.startDate && value.endDate) {
        setActivePreset(matchPreset(value.startDate, value.endDate));
        loadSummary(value.startDate, value.endDate);
        return;
      }

      if (!value.startDate && !value.endDate) {
        setActivePreset("all");
        loadSummary();
      }
    });
    return () => unsubscribe();
  }, [watch, loadSummary]);

  const metrics = useMemo(() => {
    const total = getSummaryValue(summary, "totalSum");
    const collected = getSummaryValue(summary, "paid", true);
    const outstanding = getSummaryValue(summary, "dueSum");
    const discount = getSummaryValue(summary, "discountSum");
    const net =
      Number(summary.netotalSum ?? 0) ||
      Math.max(total - discount, 0);
    const collectionRate = total > 0 ? Math.min(100, (collected / total) * 100) : 0;
    const avgInvoice = invoiceCount > 0 ? total / invoiceCount : 0;

    const cash = getSummaryValue(summary, "cashSum");
    const card = getSummaryValue(summary, "cardSum");
    const transfer = getSummaryValue(summary, "bankTransferSum");
    const paidTotal = cash + card + transfer;

    const paymentMix = [
      { label: "Cash", value: cash, color: "bg-emerald-500" },
      { label: "Card", value: card, color: "bg-indigo-500" },
      { label: "Transfer", value: transfer, color: "bg-sky-500" },
    ].map((item) => ({
      ...item,
      percent: paidTotal > 0 ? (item.value / paidTotal) * 100 : 0,
    }));

    const topMethod = [...paymentMix].sort((a, b) => b.value - a.value)[0];

    return {
      total,
      collected,
      outstanding,
      discount,
      net,
      collectionRate,
      avgInvoice,
      paymentMix,
      topMethod,
    };
  }, [summary, invoiceCount]);

  const insights = useMemo(() => {
    const lines: string[] = [];

    if (loading) return lines;

    lines.push(
      invoiceCount === 1
        ? "1 payment invoice in this period."
        : `${invoiceCount.toLocaleString()} payment invoices in this period.`
    );

    if (invoiceCount > 0 && metrics.avgInvoice > 0) {
      lines.push(`Average invoice value: ${formatAmount(metrics.avgInvoice)}.`);
    }

    if (metrics.collected > 0 && metrics.topMethod?.value > 0) {
      lines.push(
        `Most used payment method: ${metrics.topMethod.label} (${metrics.topMethod.percent.toFixed(0)}% of collections).`
      );
    }

    if (metrics.outstanding > 0) {
      lines.push(
        `${formatAmount(metrics.outstanding)} is still outstanding — follow up on unpaid balances.`
      );
    } else if (metrics.total > 0) {
      lines.push("All billed amounts in this period are fully collected.");
    }

    if (metrics.discount > 0 && metrics.total > 0) {
      const discountRate = (metrics.discount / metrics.total) * 100;
      lines.push(
        `Discounts represent ${discountRate.toFixed(1)}% of total billed amount.`
      );
    }

    return lines;
  }, [loading, invoiceCount, metrics]);

  const onSubmit: SubmitHandler<FormData> = useCallback(async (data) => {
    if (!data.startDate || !data.endDate) {
      toast.info("Select both From and To dates to download the report.");
      return;
    }
    setDownloading(true);
    try {
      const response = await baseUrl.get("client/payment/payment-report", {
        responseType: "blob",
        params: {
          startDate: data.startDate,
          endDate: data.endDate,
        },
      });
      if (response.status === 204) {
        toast.info("No data found for the selected period.");
        return;
      }
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(pdfBlob);
      link.download = `payment-report-${data.startDate}-to-${data.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      toast.error("Could not download the report.");
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleReset = (e: React.MouseEvent) => {
    e.preventDefault();
    applyPreset("all");
  };

  const rangeLabel = useMemo(() => {
    if (activePreset && matchPreset(startDate, endDate) === activePreset) {
      return getPresetLabel(activePreset);
    }
    if (startDate && endDate) return `${startDate} → ${endDate}`;
    return "All time";
  }, [activePreset, startDate, endDate]);

  return (
    <div className="space-y-6">
      <PanelCard className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Filter summary
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Showing:{" "}
              <span className="font-semibold text-slate-900 dark:text-white">
                {rangeLabel}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  activePreset === preset.id
                    ? "bg-primary text-white shadow-sm shadow-primary/20"
                    : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </PanelCard>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-primary/[0.04] p-6 dark:border-slate-700/80 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-primary/10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
              <AssessmentOutlinedIcon sx={{ fontSize: 26 }} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                Clinic payments
              </p>
              <p className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                Payment summary & report
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Period:{" "}
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {rangeLabel}
                </span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:min-w-[280px]">
            <div className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Invoices
              </p>
              <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                {loading ? "…" : invoiceCount.toLocaleString()}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Avg invoice
              </p>
              <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                {loading ? "…" : formatAmount(metrics.avgInvoice)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60 col-span-2 sm:col-span-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Collection rate
              </p>
              <p className="text-lg font-bold tabular-nums text-primary">
                {loading ? "…" : `${metrics.collectionRate.toFixed(1)}%`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
          Financial overview
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          {FINANCIAL_CARDS.map((card) => (
            <SummaryCardTile
              key={card.key}
              card={card}
              loading={loading}
              value={getSummaryValue(summary, card.key, card.computed)}
            />
          ))}
        </div>
      </div>

      <PaymentReportCharts summary={summary} trend={trend} loading={loading} />

      <div className="grid gap-6 lg:grid-cols-2">
        <PanelCard className="p-5 sm:p-6">
          <SectionHeader
            icon={ReceiptLongOutlinedIcon}
            title="Payment methods"
            subtitle="How patients paid during this period"
          />
          <div className="grid grid-cols-3 gap-3 mb-5">
            {METHOD_CARDS.map((card) => (
              <SummaryCardTile
                key={card.key}
                card={card}
                loading={loading}
                value={getSummaryValue(summary, card.key)}
              />
            ))}
          </div>
          <div className="space-y-3">
            {metrics.paymentMix.map((item) => (
              <div key={item.label}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium text-slate-600 dark:text-slate-300">
                    {item.label}
                  </span>
                  <span className="tabular-nums text-slate-500 dark:text-slate-400">
                    {formatAmount(item.value)} ({item.percent.toFixed(0)}%)
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700/60">
                  <div
                    className={`h-full rounded-full ${item.color} transition-all duration-500`}
                    style={{ width: `${item.percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </PanelCard>

        <PanelCard className="p-5 sm:p-6">
          <SectionHeader
            icon={InsightsOutlinedIcon}
            title="At a glance"
            subtitle="Key takeaways for the selected period"
          />
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="font-medium text-slate-600 dark:text-slate-300">
                Collection progress
              </span>
              <span className="font-semibold tabular-nums text-primary">
                {loading ? "…" : `${metrics.collectionRate.toFixed(1)}%`}
              </span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700/60">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500"
                style={{ width: `${metrics.collectionRate}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              {formatAmount(metrics.collected)} collected of{" "}
              {formatAmount(metrics.total)} billed
            </p>
          </div>

          <ul className="space-y-2.5">
            {loading ? (
              <li className="text-sm text-slate-400">Loading insights…</li>
            ) : insights.length === 0 ? (
              <li className="text-sm text-slate-400">No payment data yet.</li>
            ) : (
              insights.map((line) => (
                <li
                  key={line}
                  className="flex gap-2 text-sm text-slate-600 dark:text-slate-300"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <span>{line}</span>
                </li>
              ))
            )}
          </ul>

          {metrics.outstanding > 0 && !loading ? (
            <div className="mt-4 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
              <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} className="shrink-0 mt-0.5" />
              <span>
                Outstanding balance needs attention. Export the PDF report to review
                invoices with remaining due amounts.
              </span>
            </div>
          ) : null}
        </PanelCard>
      </div>

      <PanelCard className="p-5 sm:p-6 xl:p-7">
        <SectionHeader
          icon={PictureAsPdfOutlinedIcon}
          title="Download payment report"
          subtitle="Generate a PDF with invoice-level detail for accounting or review"
        />

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <div className="flex flex-wrap gap-3">
              <FormDate label="From" name="startDate" />
              <FormDate label="To" name="endDate" />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={downloading}
                className="inline-flex h-9 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white shadow-md shadow-primary/20 transition hover:opacity-95 disabled:opacity-60"
              >
                <PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} />
                {downloading ? "Preparing…" : "Download PDF"}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex h-9 items-center rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Reset dates
              </button>
            </div>
          </form>
        </FormProvider>
        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          PDF export uses the same date range as the summary filter above, or pick
          custom dates below. The report lists each invoice, patient, dentist, and
          payment breakdown.
        </p>
      </PanelCard>
    </div>
  );
}
